import axios from 'axios';
import server from 'util/Server';
import { 
  IParametrizacionEDS,
  IParametrizacionListResponse,
  IUpdateFrecuenciaRequest,
  IAuditoriaCierre,
  IAuditoriaListResponse,
  IBatchRunRequest,
  IBatchRunResponse,
  ICreateParametrizacionEDS,
  IUpdateParametrizacionEDS,
  ISignedUrlRequest,
  ISignedUrlResponse
} from '../interfaces/parametrizacionEDS.interface';

class ParametrizacionEDSService {

  // URL base del servicio batch - configurada para HTTPS
  private static baseURL = `${process.env.REACT_APP_HOST}${process.env.REACT_APP_INTERFAZ_ERP}`;

  private static getHeaders() {
    return {
      'Authorization': server.obtenerToken(),
      'Content-Type': 'application/json',
    };
  }

  /**
   * Obtener lista de parametrizaciones de EDS
   * Automáticamente filtra por empresa_id según el usuario en sesión (middleware backend)
   */
  static async getParametrizaciones(
    skip: number = 0,
    limit: number = 100,
    solo_activas: boolean = false
  ): Promise<IParametrizacionListResponse> {
    try {
      const response = await axios.get<IParametrizacionListResponse>(
        `${this.baseURL}/api/v1/eds/parametrizaciones`,
        {
          headers: this.getHeaders(),
          params: { skip, limit, solo_activas }
        }
      );

      return response.data;
    } catch (error) {
      console.error('::error en getParametrizaciones::', error);
      //if 401?
      console.log('::error.response.status::', (error as any)?.response?.status);
      throw error;
    }
  }

  /**
   * Crear nueva parametrización de EDS
   * Solo para administradores
   */
  static async createParametrizacion(data: ICreateParametrizacionEDS): Promise<IParametrizacionEDS> {
    try {
      const response = await axios.post<IParametrizacionEDS>(
        `${this.baseURL}/api/v1/eds/parametrizaciones`,
        data,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating parametrizacion:', error);
      throw error;
    }
  }

  /**
   * Actualizar parametrización de EDS
   * Solo admins o el administrador de esa EDS específica pueden actualizar
   */
  static async updateParametrizacion(
    id: number,
    data: IUpdateParametrizacionEDS
  ): Promise<IParametrizacionEDS> {
    try {
      const response = await axios.patch<IParametrizacionEDS>(
        `${this.baseURL}/api/v1/eds/parametrizaciones/${id}`,
        data,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating parametrizacion:', error);
      throw error;
    }
  }

  /**
   * Actualizar frecuencia y estado de una parametrización EDS
   * Solo admins o el administrador de esa EDS específica pueden actualizar
   */
  static async updateFrecuencia(
    eds_id: number,
    data: IUpdateFrecuenciaRequest
  ): Promise<IParametrizacionEDS> {
    try {
      const response = await axios.patch<IParametrizacionEDS>(
        `${this.baseURL}/api/v1/eds/parametrizaciones/${eds_id}`,
        data,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating frecuencia:', error);
      throw error;
    }
  }

  /**
   * Obtener auditorías de cierres
   * Automáticamente filtra por empresa_id según el usuario en sesión
   */
  static async getAuditorias(
    limit: number = 50,
    offset: number = 0
  ): Promise<IAuditoriaListResponse> {
    try {
      const response = await axios.get<IAuditoriaListResponse>(
        `${this.baseURL}/api/v1/eds/auditorias`,
        {
          headers: this.getHeaders(),
          params: { limit, offset }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching auditorias:', error);
      throw error;
    }
  }

  /**
   * Ejecutar batch manualmente
   * Solo para administradores
   */
  static async ejecutarBatch(data?: IBatchRunRequest): Promise<IBatchRunResponse> {
    try {
      const response = await axios.post<IBatchRunResponse>(
        `${this.baseURL}/api/v1/batch/run`,
        data || { force_all: false },
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error ejecutando batch:', error);
      throw error;
    }
  }

  /**
   * Health check del servicio
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await axios.get(
        `${this.baseURL}/health`,
        { headers: this.getHeaders() }
      );

      return response.status === 200;
    } catch (error) {
      console.error('Error en health check:', error);
      return false;
    }
  }

  /**
   * Obtener estadísticas calculadas desde las parametrizaciones
   */
  static async getStatistics(): Promise<{
    total: number;
    activas: number;
    inactivas: number;
    frecuenciasDiferentes: { [key: string]: number };
  }> {
    try {
      const data = await this.getParametrizaciones(0, 1000, false);
      
      const stats = {
        total: data.total,
        activas: data.parametrizaciones.filter(p => p.activo).length,
        inactivas: data.parametrizaciones.filter(p => !p.activo).length,
        frecuenciasDiferentes: data.parametrizaciones.reduce((acc, p) => {
          acc[p.frecuencia] = (acc[p.frecuencia] || 0) + 1;
          return acc;
        }, {} as { [key: string]: number })
      };

      return stats;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Generar signed URL para descarga de archivos de cierres
   */
  static async generateSignedUrl(data: ISignedUrlRequest): Promise<ISignedUrlResponse> {
    try {
      const response = await axios.post<ISignedUrlResponse>(
        `${this.baseURL}/api/v1/eds/signed-url/generate`,
        data,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      throw error;
    }
  }
}

export { ParametrizacionEDSService };
